import { task, restartableTask, dropTaskGroup, restartableTaskGroup } from 'ember-concurrency';
import Controller, { inject as controller } from '@ember/controller';
import { inject as service } from '@ember/service';
import { action, computed } from '@ember/object';
import { readOnly } from '@ember/object/computed';
import { APPLICATION_STATUS } from 'sh-www/models/application';
import { EMBER_ROUTE_NAMES } from 'sh-www/constants/routes';
import { PROFILE_PROJECTDIGEST_INCLUDES } from 'sh-www/constants/components/profile';
import { v1 } from 'ember-uuid';

const QUERY_PARAMS = Object.freeze(['sort', 'secondary_sort', 'filter', 'search']);

const APPLICATION_INCLUDES = [
  'assignment',
  'brand.image',
  'buyer',
  'seller',
  'company',
  'legal-agreements.legal',
  'thread',
  ...PROFILE_PROJECTDIGEST_INCLUDES.map((x) => `projectdigest.${x}`),
];

const APPLICATION_INCLUDES_STRING = APPLICATION_INCLUDES.join(',');

/**
 * @param {{}} params
 * @return {string}
 */
const serializeParams = (params) => QUERY_PARAMS.reduce((accum, key) => {
  const value = params[key];

  if (value) {
    accum.push(`${key}=${value}`);
  }

  return accum;
}, []).join('&');

export const createCache = (queryParams) => {
  const cachedParams = { ...queryParams };
  const cache = new Map();
  const cacheKey = v1();

  return Object.freeze({
    get sort() {
      return cachedParams.sort || 'last-activity';
    },

    get secondarySort() {
      return cachedParams.secondary_sort || 'desc';
    },

    get filter() {
      return cachedParams.filter || '';
    },

    get search() {
      return cachedParams.search || '';
    },

    get cacheKey() {
      return cacheKey;
    },

    getPage(page) {
      return cache.get(page);
    },

    setPage(page, value) {
      cache.set(page, value);
      return value;
    },

    isValid(queryParams) {
      return serializeParams(cachedParams) === serializeParams(queryParams);
    },
  });
}

const mapSelectedOptions = (options, selected) => Object.entries(options).map(([title, value]) => ({
  title,
  value,
  selected: value === selected,
}));

export const sellerProjectsViewControllerFactory = ({ emptyText, sortOptions, defaultFilter = null, filterOptions = { } }) => {
  return class SellerProjectsViewController extends Controller {
    @service('commands.application-commands')
    applicationCommandService;

    @service('commands.thread-commands')
    threadCommandService;

    @service('state-managers.application')
    applicationStateManager;

    @service api;
    @service router;
    @service store;

    queryParams = QUERY_PARAMS;
    sort = null;
    secondary_sort = null;
    filter = null;
    search = null;

    data = [];
    meta = null;
    cache = null;

    get emptyText() {
      return emptyText;
    }

    get showResults() {
      return true;
    }

    @readOnly('cache.sort')
    sortValue;

    @readOnly('cache.secondarySort')
    secondarySortValue;

    @readOnly('cache.filter')
    filterValue;

    @dropTaskGroup
    modelUpdates;

    @restartableTaskGroup
    modelInvalidation;

    @readOnly('modelUpdates.isRunning')
    loading;

    @computed('meta.pagination.total')
    get total() {
      return this.meta?.pagination.total ?? 0;
    }

    @computed('meta.pagination.total_pages')
    get totalPages() {
      return this.meta?.pagination.total_pages ?? 0;
    }

    @computed('meta.pagination.current_page')
    get currentPage() {
      return this.meta?.pagination.current_page ?? 0;
    }

    @computed('meta.pagination.per_page')
    get pageSize() {
      return this.meta?.pagination.per_page ?? 0;
    }

    @computed('cache.sort')
    get sortOptions() {
      return mapSelectedOptions(sortOptions, this.cache.sort);
    }

    @computed('cache.filter')
    get filterOptions() {
      return mapSelectedOptions(filterOptions, this.cache.filter);
    }

    @action
    sortValueChanged(sort) {
      this.setProperties({ sort, secondary_sort: null });
    }

    @action
    secondarySortValueChanged(secondarySort) {
      this.set('secondary_sort', secondarySort);
    }

    @action
    filterValueChanged(filter) {
      this.set('filter', filter || null);
    }

    modelIsValid(queryParams) {
      return this.cache.isValid(queryParams);
    }

    /**
     * @param {Application} application
     * @returns {*}
     */
    @task
    *markComplete(application) {
      this.router.transitionTo(EMBER_ROUTE_NAMES.PROJECT__COMPLETE, application.id);
    }

    /**
     * @param {Application} application
     * @returns {*}
     */
    @task
    *confirmCancellation(application) {
      this.router.transitionTo(EMBER_ROUTE_NAMES.PROJECT__CANCELLATION__PAYMENT, application.id);
    }

    /**
     * @param {Application} application
     * @returns {*}
     */
    @task
    *requestAdvance(application) {
      this.router.transitionTo(EMBER_ROUTE_NAMES.PROJECT__ADVANCE, application.id);
    }

    /**
     * @param {Application} application
     * @returns {*}
     */
    @task
    *reviewAdvance(application) {
      this.router.transitionTo(EMBER_ROUTE_NAMES.PROJECT__ADVANCE__APPROVE, application.id);
    }

    /**
     * @param {Application} application
     * @param {string} message
     * @returns {*}
     */
    @task
    *declineApplication(application, message) {
      if (message) {
        yield this.threadCommandService.addNewMessageToThread(application.thread, message, []);
      }

      yield this.applicationCommandService.declineApplication(application);
      yield this.#loadApplication(application);

      this.applicationStateManager.addToast('Project declined');
    }

    /**
     * @param {Application} application
     * @returns {*}
     */
    @task
    *acceptOffer(application) {
      // accepting a legal agreement can update the status of the application behind the scenes,
      // making it active by the time this method is called.
      if (application.status !== APPLICATION_STATUS.ACTIVE) {
        yield this.applicationCommandService.approveOffer(application.id, APPLICATION_INCLUDES);
        yield this.#loadApplication(application);
      }

      this.applicationStateManager.addToast('Project started');
    }

    /**
     * @param {Application} application
     * @param {Number} price
     * @returns {*}
     */
    @task
    *counterOffer(application, price) {
      yield this.applicationCommandService.makeOffer(application.id, price, APPLICATION_INCLUDES);
      yield this.#loadApplication(application);
      this.applicationStateManager.addToast('Offer sent');
    }

    /**
     * @param {Application} application
     * @returns {*}
     */
    @task
    *cancelAdvance(application) {
      yield this.applicationCommandService.cancelPendingAdvances(application);
      this.applicationStateManager.addToast('Advance cancelled');
    }

    /**
     * @param {Application} application
     * @param {Legal} legal
     * @returns {*}
     */
    @task
    *agreeToLegal(application, legal) {
      yield this.applicationCommandService.agreeToLegal(application, legal);
      yield this.#loadApplication(application);
    }

    @task({ group: 'modelUpdates' })
    *initializeModel(queryParams) {
      this.set('cache', createCache(queryParams));
      yield this.updateModel.perform(1);
    }

    @task({ group: 'modelUpdates' })
    *updatePage(increment) {
      const newPage = this.currentPage + increment;

      if ((newPage > 0) && (newPage <= this.totalPages)) {
        yield this.updateModel.perform(newPage);
      }
    }

    @task({ group: 'modelInvalidation' })
    *reloadModel(queryParams) {
      yield this.modelUpdates.cancelAll();
      this.initializeModel.perform(queryParams);
    }

    @task({ group: 'modelInvalidation' })
    *invalidateModel() {
      yield this.modelUpdates.cancelAll();

      const queryParams = QUERY_PARAMS.reduce((accum, param) => {
        accum[param] = null;
        return accum;
      }, { });

      this.setProperties({
        ...queryParams,
        data: [],
        meta: null,
        cache: null,
      });
    }

    /**
     * @param {number} page
     * @returns {Generator<*, void, *>}
     */
    @restartableTask
    *updateModel(page) {
      const cache = this.cache;
      const cachedResponse = cache.getPage(page);

      if (cachedResponse) {
        this.setProperties(cachedResponse);
        return;
      }

      const params = {
        page,
        sort: cache.sort,
        secondary_sort: cache.secondarySort,
        cache_key: cache.cacheKey,
        format: 'default',
        count: 10,
        filter: { },
        include: APPLICATION_INCLUDES_STRING,
      };

      if (cache.search) {
        params.search = cache.search;
      } else if (cache.filter) {
        params.filter.status = cache.filter;
      }

      if (defaultFilter) {
        params.filter.simple_status = defaultFilter;
      }

      const response = yield this.api.request('applications', { params });
      const store = this.store;
      store.pushPayload(response);

      const data = response.data.map(({ id }) => store.peekRecord('application', id));
      const meta = response.meta;
      cache.setPage(page, { data, meta });

      this.setProperties({ data, meta });
    }

    #loadApplication(application) {
      return this.applicationCommandService
        .loadApplication(application.id, APPLICATION_INCLUDES)
        .then((response) => this.store.pushPayload(response));
    }
  };
};

/**
 * @param {string} name
 * @return {Ember.ComputedProperty}
 */
const pageTotal = (name) => readOnly(`${name}Controller.meta.pagination.total`);

/**
 * @param name
 * @return {Ember.ComputedProperty}
 */
const isRoute = (name) => computed('router.currentRouteName', function() {
  switch (this.router.currentRouteName) {
    case `projects.${name}`:
    case `projects.${name}_loading`:
      return true;

    default:
      return false;
  }
});

const SEARCH_QUERY = Object.freeze({
  sort: null,
  secondary_sort: null,
  filter: null,
  search: null,
});

class SellerProjectsController extends Controller {
  @service router;

  @controller('projects.active')
  activeController;

  @controller('projects.archived')
  archivedController;

  @controller('projects.search')
  searchController;

  @pageTotal('active')
  activeCount;

  @isRoute('active')
  isActiveRoute;

  @isRoute('archived')
  isArchivedRoute;

  @isRoute('search')
  isSearchRoute;

  get searchQuery() {
    return SEARCH_QUERY;
  }

  @action
  onClose() {
    this.router.transitionTo(EMBER_ROUTE_NAMES.PROJECTS__ACTIVE);
  }
}

export default SellerProjectsController;
